import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { isNil } from 'lodash';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useEmployees from '@ubeya/shared/hooks/employees/useEmployees';
import { mappedArray } from '@ubeya/shared/utils/array';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import HourlyBookingModal from '../WeekView/Modals/HourlyBookingModal';
import MarkAsOpenShiftPrompt from '../MarkAsOpenShiftPrompt';
import { BOOKING_ACTIONS, DRIVER_POSITION_SLUG, TIME_AVAILABILITY_BOOKING_OPTIONS } from 'scheduling/constants';
import { getClockingValues } from 'scheduling/utils';

const EditSpecificBookingModal = ({
  slot,
  bookingActionsFactory,
  getEmployeesShifts,
  validate,
  projects,
  selectedBranchId,
  defaultTimePickerOptions,
  interval,
  refetch,
  mappedLabels,
  labels,
  onClose,
  userPermissions: { hasActionPermission }
}) => {
  const {
    shiftId,
    startTime,
    labelId,
    endTime,
    timesheet,
    notes,
    slotId,
    positionId,
    locationId,
    clientBranchId,
    branchCostCenterId,
    clientAdminId,
    employeeId,
    date,
    projectId,
    employeeSlotId,
    isOpenShift: shiftIsOpenShift
  } = slot || {};

  const [showMarkAsOpenShiftPrompt, setShowMarkAsOpenShiftPrompt] = useState(false);

  const { positions, mappedPositions } = usePositions();
  const { labelOptions } = useLabels();

  const defaultPositionId = useMemo(
    () => positions?.find(({ prevSlug }) => prevSlug === DRIVER_POSITION_SLUG)?.id || null,
    [positions]
  );

  const { data: employeeRawData, refetch: refetchEmployees } = useEmployees({
    branchIds: [selectedBranchId],
    pageSize: 400
  });

  const employeesMap = useMemo(() => mappedArray(employeeRawData || [], ({ id }) => id), [employeeRawData]);

  const openShiftsByDate = useMemo(
    () =>
      (projects || []).reduce((all, project) => {
        if (!all[project.date]) {
          all[project.date] = [];
        }

        all[project.date].push(
          ...project.shifts
            .filter((shift) => shift.needed > shift.stats?.bookedStandBy + shift.stats?.booked)
            .map((shift) => ({
              ...shift,
              leftToBook: shift.needed - (shift?.stats?.bookedStandBy + shift?.stats?.booked) || 0,
              date: project.date,
              label: mappedLabels[project.labelId],
              position: mappedPositions[shift.positionId]
            }))
        );
        return all;
      }, {}),
    [mappedLabels, mappedPositions, projects]
  );

  const handleDeleteEmployeeBookingFromShift = useCallback(
    async ({ shouldRefetch = true, markAsOpenShift = true }) => {
      await bookingActionsFactory({
        type: BOOKING_ACTIONS.deleteBookedShift,
        values: {
          projectId,
          shiftId,
          slotId,
          markAsOpenShift,
          decreaseNeeded: !markAsOpenShift
        },
        refetchProjects: shouldRefetch
      });
    },
    [bookingActionsFactory, projectId, shiftId, slotId]
  );

  const onDeleteEmployeeBookingFromShift = useCallback(
    async ({ markAsOpenShift = true }) => {
      await handleDeleteEmployeeBookingFromShift({ markAsOpenShift, shouldRefetch: true });
      onClose();
    },
    [handleDeleteEmployeeBookingFromShift, onClose]
  );

  const initialValues = useMemo(() => {
    if (!slot) {
      return {};
    }

    return {
      startTime,
      locationId,
      clientBranchId,
      branchCostCenterId,
      clientAdminId,
      endTime,
      positionId,
      employeeId,
      labelId: labelId || null,
      date,
      bookingOption: TIME_AVAILABILITY_BOOKING_OPTIONS.BY_LABEL,
      projectId,
      clockIn: timesheet?.startTime || null,
      clockOut: timesheet?.endTime || null,
      notes,
      breaktimes: timesheet?.breaktimes || [],
      employeeSlotId,
      isOpenShift: shiftIsOpenShift,
      shiftId
    };
  }, [
    slot,
    startTime,
    locationId,
    clientBranchId,
    branchCostCenterId,
    clientAdminId,
    endTime,
    positionId,
    employeeId,
    labelId,
    date,
    projectId,
    timesheet?.startTime,
    timesheet?.endTime,
    timesheet?.breaktimes,
    notes,
    employeeSlotId,
    shiftId,
    shiftIsOpenShift
  ]);

  const handleCreateNewShift = useCallback(
    async ({ dateProp, values, shouldRefetch = false, isOpenShift = true }) => {
      const { startTime: startTimeByDate, endTime: endTimeByDate } = getTimeRangeWithDates({
        startTime: values.startTime,
        endTime: values.endTime,
        date: dateProp
      });

      const newShiftLabelId =
        (values.labelId && parseInt(values.labelId, 10)) ||
        (labelOptions?.length === 1 ? labelOptions[0]?.value : undefined);

      return bookingActionsFactory({
        type: BOOKING_ACTIONS.bookToNewShift,
        values: {
          // project level
          labelId: newShiftLabelId,
          locationId: values.locationId ?? mappedLabels[values.labelId]?.locationId ?? undefined,
          clientBranchId: values.clientBranchId,
          clientAdminId: values.clientAdminId,

          // shift level
          branchCostCenterId: values.branchCostCenterId,
          date: dateProp,
          positionId: values.positionId || defaultPositionId,
          employeeId: values.employeeId,
          startTime: startTimeByDate,
          endTime: endTimeByDate,
          isOpenShift
        },
        refetchProjects: shouldRefetch
      });
    },
    [bookingActionsFactory, defaultPositionId, labelOptions, mappedLabels]
  );

  const handleChangedEmployeeId = useCallback(
    async ({ newEmployeeId }) => {
      //book new employee to the same shift
      if (newEmployeeId) {
        // unbook previous
        await handleDeleteEmployeeBookingFromShift({ shouldRefetch: false, markAsOpenShift: false });

        await bookingActionsFactory({
          type: BOOKING_ACTIONS.bookToExistingShift,
          values: { projectId, shiftId, employeeId: newEmployeeId },
          refetchProjects: true
        });

        return;
      }

      // if no other employee was selected, mark as open shift
      setShowMarkAsOpenShiftPrompt(true);

      return { shouldClose: false };
    },
    [bookingActionsFactory, handleDeleteEmployeeBookingFromShift, projectId, shiftId]
  );

  const handleUpdateTimesheet = useCallback(
    async ({
      timesheetId,
      clockingValuesProp: { clockIn, clockOut } = {},
      shiftProp,
      notes: timesheetNotes,
      breaktimes
    }) => {
      await bookingActionsFactory({
        type: BOOKING_ACTIONS.handleUpdateTimesheet,
        values: {
          projectId,
          date,
          timesheetId,
          clockIn,
          clockOut,
          shiftId: shiftProp.shiftId,
          slotId: shiftProp.slotId,
          notes: timesheetNotes,
          breaktimes
        },
        refetchProjects: false
      });

      await refetch();
    },
    [bookingActionsFactory, date, projectId, refetch]
  );

  const handleSubmitUseCases = useCallback(
    async ({ values, form }) => {
      if (!slot) {
        return null;
      }

      const { dirtyFields } = form.getState();

      const dirtyActualBreakTimes = values.breaktimes;

      const shouldCreateOrUpdateActualBreakTimes =
        dirtyActualBreakTimes?.added?.length > 0 ||
        dirtyActualBreakTimes?.edited?.length > 0 ||
        dirtyActualBreakTimes?.deleted?.length > 0;

      const breaktimes = shouldCreateOrUpdateActualBreakTimes ? dirtyActualBreakTimes : undefined;

      const employeeAssignedToShift = !isNil(values?.employeeId);

      const { startTime: startTimeFromRange, endTime: endTimeFromRange } = getTimeRangeWithDates({
        startTime: values.startTime,
        endTime: values.endTime,
        date
      });

      const onlyEmployeeIdChanges =
        (Object.entries(dirtyFields) || []).length === 0 && values.employeeId !== employeeId;

      if (onlyEmployeeIdChanges) {
        return handleChangedEmployeeId({ newEmployeeId: values.employeeId });
      }

      const isUpdatingPosition = values?.positionId !== slot?.positionId;
      const newEmployeeIsAssignedToShift = values?.employeeId !== employeeId && employeeAssignedToShift;

      if (isUpdatingPosition || newEmployeeIsAssignedToShift) {
        /* un book employee from previous shift */
        await handleDeleteEmployeeBookingFromShift({ shouldRefetch: false, markAsOpenShift: false });

        if (employeeAssignedToShift) {
          /* create new shift for the new employee */
          const { timesheet: newTimesheet, shiftData: newShiftData = {} } =
            (await handleCreateNewShift({ dateProp: date, values, shouldRefetch: true })) || {};

          const newClockingValues = getClockingValues({
            clockIn: values?.clockIn,
            clockOut: values?.clockOut,
            timesheet: newTimesheet,
            date
          });

          if (newTimesheet && (newClockingValues || values.notes || breaktimes)) {
            await handleUpdateTimesheet({
              timesheetId: newTimesheet.id,
              breaktimes,
              shiftProp: { shiftId: newShiftData.shiftId, slotId: newShiftData.slotId },
              clockingValuesProp: newClockingValues,
              notes: values.notes
            });
          }
          return;
        }
      }

      if (timesheet) {
        await handleUpdateTimesheet({
          timesheetId: timesheet.id,
          clockingValuesProp: getClockingValues({
            clockIn: values?.clockIn,
            clockOut: values?.clockOut,
            timesheet,
            date
          }),
          shiftProp: { shiftId, projectId, slotId }
        });
      }

      if (!employeeAssignedToShift) {
        setShowMarkAsOpenShiftPrompt(true);
        return { shouldClose: false };
      }

      await bookingActionsFactory({
        type: BOOKING_ACTIONS.editBookedShift,
        values: {
          projectId,
          notes: values.notes,
          breaktimes,
          shiftId,
          slotId,
          startTime: startTimeFromRange,
          endTime: endTimeFromRange,
          branchCostCenterId: values.branchCostCenterId
        },
        refetchProjects: true
      });

      refetch?.();
    },
    [
      slot,
      date,
      employeeId,
      timesheet,
      bookingActionsFactory,
      projectId,
      shiftId,
      slotId,
      refetch,
      handleChangedEmployeeId,
      handleDeleteEmployeeBookingFromShift,
      handleCreateNewShift,
      handleUpdateTimesheet
    ]
  );

  const handleSubmit = useCallback(
    async ({ values, form }) => {
      if (!slot) {
        return null;
      }

      const result = await handleSubmitUseCases({ values, form });
      const { shouldClose = true } = result || {};
      shouldClose && onClose();
    },
    [slot, handleSubmitUseCases, onClose]
  );

  const shiftsByEmployee = useMemo(
    () => ({
      [employeeId]: getEmployeesShifts(employeeId, moment(date).format(API_DATE_TIME_FORMAT)) || []
    }),
    [date, employeeId, getEmployeesShifts]
  );

  if (Object.keys(employeesMap).length === 0) {
    return null;
  }

  return (
    <>
      <HourlyBookingModal
        onClose={onClose}
        shiftsByEmployee={shiftsByEmployee}
        editMode
        onDelete={onDeleteEmployeeBookingFromShift}
        labels={labels}
        mappedLabels={mappedLabels}
        disableTimesChange={!hasActionPermission}
        weekView
        initialTimingValues={initialValues}
        handleSubmit={handleSubmit}
        data={initialValues}
        mappedOpenShiftsByDateAndId={{}}
        employeesMap={employeesMap}
        refetchEmployees={refetchEmployees}
        defaultTimePickerOptions={defaultTimePickerOptions}
        interval={interval}
        openShiftsByDate={openShiftsByDate}
        validate={validate}
      />
      {showMarkAsOpenShiftPrompt && (
        <MarkAsOpenShiftPrompt
          onConfirm={onDeleteEmployeeBookingFromShift}
          onClose={() => setShowMarkAsOpenShiftPrompt(false)}
        />
      )}
    </>
  );
};

export default withPermissions(EditSpecificBookingModal, {
  action: {
    accountSettings: {
      notAllowed: ['isAccountNetworkAccessSupplier']
    }
  }
});
